<template>
  <div class="preview-page">
    <StepProgress />

    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Preview
            </v-card-title>
            
            <v-card-text class="pa-6">
              <div class="mb-4">
                <p class="text-h6 mb-2">
                  Review your memorial for {{ store.personName || 'your loved one' }}
                </p>
                <p class="text-body-2 text--secondary mb-4">
                  This is how your memorial will appear to family and friends before they contribute their photos and stories.
                </p>
              </div>

              <!-- Feed-style Preview -->
              <div class="feed-preview">
                <!-- Memorial Hero Section -->
                <div class="memorial-hero">
                  <!-- Profile Photo -->
                  <div class="hero-photo-container">
                    <img
                      v-if="profilePhoto"
                      :src="profilePhoto.url"
                      :alt="store.personName"
                      class="hero-photo"
                    />
                    <div v-else class="hero-photo-placeholder">
                      <v-icon size="80" color="grey-lighten-1">mdi-account</v-icon>
                    </div>
                  </div>

                  <!-- Memorial Name -->
                  <!--<div class="remembering-title">Remembering</div>-->
                  <h1 class="hero-name">
                    {{ store.personName || 'Memorial' }}
                    <span v-if="store.birthDate && store.deathDate" class="life-dates">
                      {{ formatLifeDate(store.birthDate) }} - {{ formatLifeDate(store.deathDate) }}
                    </span>
                  </h1>
                </div>

                <!-- Memorial Details and Address -->
                <div
                  v-if="store.birthDate || store.deathDate || (store.hasEvent && (store.eventName || store.memorialDate || store.memorialAddress || store.memorialDetails))"
                  class="memorial-info mb-6"
                >
                  <div class="hero-details-panel">
                    <div v-if="store.birthDate && !store.deathDate" class="hero-detail-item">
                      <v-icon size="16" color="grey-darken-1" class="mr-1">mdi-cake-variant-outline</v-icon>
                      <span>Born {{ formatLifeDate(store.birthDate) }}</span>
                    </div>
                    <div v-if="store.deathDate && !store.birthDate" class="hero-detail-item">
                      <v-icon size="16" color="grey-darken-1" class="mr-1">mdi-flower-outline</v-icon>
                      <span>Died {{ formatLifeDate(store.deathDate) }}</span>
                    </div>
                    <div v-if="store.hasEvent && store.eventName" class="hero-detail-item">
                      <v-icon size="16" color="grey-darken-1" class="mr-1">mdi-calendar-star</v-icon>
                      <span>{{ store.eventName }}</span>
                    </div>
                    <div v-if="store.hasEvent && store.memorialDate" class="hero-detail-item">
                      <v-icon size="16" color="grey-darken-1" class="mr-1">mdi-calendar</v-icon>
                      <span>{{ formatDate(store.memorialDate) }}</span>
                    </div>
                    <div v-if="store.hasEvent && store.memorialAddress" class="hero-detail-row">
                      <div class="hero-detail-item address-detail">
                        <v-icon size="16" color="grey-darken-1" class="mr-1">mdi-map-marker</v-icon>
                        <span>{{ store.memorialAddress }}</span>
                      </div>
                      <img
                        src="/images/map-icon.png"
                        alt=""
                        class="map-mini-icon"
                      />
                    </div>
                    <div v-if="store.hasEvent && store.memorialDetails" class="hero-detail-item hero-details-text">
                      <span>{{ store.memorialDetails }}</span>
                    </div>
                  </div>

                  <!-- Google Maps embed -->
                  <div v-if="store.hasEvent && store.memorialAddress && store.memorialAddress.trim()" class="hero-map mt-4">
                    <iframe
                      :src="`https://www.google.com/maps?q=${encodeURIComponent(store.memorialAddress.trim())}&output=embed`"
                      width="100%"
                      height="250"
                      style="border:0; border-radius: 8px;"
                      allowfullscreen
                      loading="lazy"
                      referrerpolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                </div>

                <!-- Feed Content -->
                <div class="feed-content">
                  <!-- Owner's Photos - Photo left, caption right -->
                  <div v-if="nonProfilePhotos.length > 0" class="photos-list mb-5">
                    <div
                      v-for="(photo, index) in nonProfilePhotos"
                      :key="index"
                      class="photo-row"
                    >
                      <div class="photo-left">
                        <img
                          :src="photo.url"
                          :alt="photo.caption || 'Memorial photo'"
                          class="photo-image"
                        />
                      </div>
                      <div v-if="photo.caption" class="photo-right">
                        <p class="photo-caption">{{ photo.caption }}</p>
                      </div>
                    </div>
                  </div>

                  <div v-if="previewAudios.length" class="mb-5">
                    <AudioEntry v-for="audio in previewAudios" :key="audio.id || audio.name" :media="audio" :memorial-slug="store.shareSlug" class="mb-4" />
                  </div>

                  <!-- Owner's Stories - Full width -->
                  <div v-if="validStories.length > 0" class="stories-section mb-5">
                    <div
                      v-for="(story, index) in validStories"
                      :key="index"
                      class="story-item"
                    >
                      <p class="story-text">{{ story }}</p>
                    </div>
                  </div>

                  <!-- Personal Message -->
                  <div v-if="store.personalMessage" class="personal-message-section mb-5">
                    <div class="message-card">
                      <p class="message-text">{{ store.personalMessage }}</p>
                    </div>
                  </div>

                  <!-- Empty State -->
                  <div v-if="nonProfilePhotos.length === 0 && previewAudios.length === 0 && validStories.length === 0 && !store.personalMessage" class="empty-state text-center py-8">
                    <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-image-off</v-icon>
                    <p class="text-body-2 text--secondary mb-2">No content yet</p>
                    <p class="text-caption text--secondary">Add photos and stories to see them here.</p>
                  </div>
                </div>
              </div>

            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="previousStep"
                class="mr-4"
              >
                <v-icon left>mdi-arrow-left</v-icon>
                Back
              </v-btn>

              <v-spacer></v-spacer>

              <!-- Next Step button (always visible when not in edit mode) -->
              <v-btn
                v-if="showNextStep"
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                @click="nextStep"
                class="px-8"
              >
                Next Step
                <v-icon right class="ml-2">mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import StepProgress from '../components/StepProgress.vue'
import { formatLifeDate } from '../utils/helpers'
import AudioEntry from '../components/media/AudioEntry.vue'

const router = useRouter()
const store = useMemorialStore()

// Local state for profile photo preview
const localProfilePhotoUrl = ref(null)

// Computed properties
const validStories = computed(() => {
  return store.stories.filter(story => story && story.trim())
})

const profilePhoto = computed(() => {
  // If there's a locally uploaded file (for non-logged-in users), use that
  if (localProfilePhotoUrl.value) {
    return { url: localProfilePhotoUrl.value }
  }
  // Otherwise, use the server-loaded profile photo
  return store.profilePhoto
})

const nonProfilePhotos = computed(() => {
  // All photos from the photos array (these are regular memorial photos, not the profile photo)
  return store.photos || []
})

const previewAudios = computed(() => (store.audios || []).map(audio => ({
  ...audio,
  title: audio.caption || audio.serverData?.title || '',
  transcript: audio.serverData?.transcript,
  transcriptionStatus: audio.serverData?.transcriptionStatus
})))

const isEditMode = computed(() => {
  // User is in edit mode if they are logged in (completed account creation)
  // AND have an existing memorial they're editing
  return store.isAccountCreated && store.isEmailVerified && store.memorialUuid
})

const showNextStep = computed(() => {
  return !isEditMode.value || store.maxReachedStep <= 4
})

// Methods
const formatDate = (dateString) => {
  if (!dateString) return ''
  try {
    return new Date(dateString).toLocaleDateString()
  } catch (error) {
    return dateString
  }
}

// Navigation
const nextStep = () => {
  store.goToStep(5) // Save step
  router.push('/save')
}

const previousStep = () => {
  store.goToStep(3) // Message step
  router.push('/message')
}

// Lifecycle
onMounted(async () => {
  // If user has a memorial UUID, reload from server to ensure fresh data
  if (store.memorialUuid && store.isAccountCreated && store.isEmailVerified) {
    console.log('Reloading memorial data from server...')
    const loaded = await store.loadUserMemorialFromServer()
    if (loaded) {
      console.log('Memorial data reloaded successfully')
    }
  }

  // Load locally uploaded profile photo (for non-logged-in users)
  if (store.profilePhotoFile) {
    const reader = new FileReader()
    reader.onload = (e) => {
      localProfilePhotoUrl.value = e.target.result
    }
    reader.readAsDataURL(store.profilePhotoFile)
  }

  store.goToStep(4) // Set current step to Preview

  // Initialize page tracking for logged-in users
  if (store.isAccountCreated && store.isEmailVerified && store.apiToken) {
    store.enterPage('preview')
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;

.preview-page {
  @include page-container;
}

.main-card {
  @include main-card;

  .v-card-title {
    @include section-header;
  }
}

/* Feed Preview Container */
.feed-preview {
  background: linear-gradient(180deg, var(--v-theme-background) 0%, var(--v-theme-surface) 100%);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid var(--v-theme-border);
}

/* Memorial Hero Section */
.memorial-hero {
  text-align: center;
  padding: 0;
  margin-bottom: 0;
}

.remembering-title {
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--v-theme-gray500);
  margin-bottom: 0.35rem;
}

.hero-photo-container {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.hero-photo {
  width: 200px;
  height: 200px;
  object-fit: cover;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.hero-photo-placeholder {
  width: 200px;
  height: 200px;
  background: var(--v-theme-gray100);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.hero-name {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem 0.6rem;
  font-family: var(--v-theme-headingFont);
  font-size: 1.75rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0 0 0.75rem 0;
}

.life-dates {
  color: var(--v-theme-gray500);
  font-size: 1rem;
  white-space: nowrap;
}

.hero-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
}

.memorial-info {
  text-align: center;
}

.hero-map {
  max-width: 700px;
  margin: 0 auto;
}

.hero-detail-item {
  display: flex;
  align-items: center;
  color: var(--v-theme-gray500);
  font-size: 0.875rem;
}

.hero-details-panel {
  background: rgba(var(--v-theme-primary), 0.05);
  padding: 0.7rem 1.5rem;
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.hero-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
}

.address-detail {
  flex: 1;
  justify-content: center;
}

.map-mini-icon {
  width: 24px;
  height: 24px;
  display: block;
  flex-shrink: 0;
}

.hero-details-text {
  margin-top: 0.15rem;
  text-align: left;
  line-height: 1.5;
  color: var(--v-theme-gray600);
}

/* Feed Content */
.feed-content {
  max-width: 100%;
}

/* Photos List - Photo left, caption right */
.photos-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.photo-row {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.photo-left {
  flex-shrink: 0;
  width: 200px;
}

.photo-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.photo-right {
  flex: 1;
  padding-top: 0;
  align-self: flex-start;
}

.photo-caption {
  font-size: 0.875rem;
  color: var(--v-theme-gray600);
  line-height: 1.5;
  margin: 0;
  padding-top: 0;
}

/* Stories Section - Full width */
.stories-section {
  width: 100%;
}

.story-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--v-theme-border);
}

.story-item:last-child {
  border-bottom: none;
}

.story-text {
  color: var(--v-theme-gray600);
  line-height: 1.6;
  white-space: pre-wrap;
  margin: 0;
  font-size: 0.875rem;
}

/* Personal Message */
.personal-message-section {
  width: 100%;
}

.message-card {
  background: rgba(var(--v-theme-primary), 0.05);
  padding: 0.875rem 1rem;
  border-radius: 10px;
  border-left: 3px solid rgb(var(--v-theme-primary));
}

.message-text {
  margin: 0;
  color: var(--v-theme-gray600);
  line-height: 1.5;
  font-size: 0.875rem;
  white-space: pre-wrap;
}

/* Empty State */
.empty-state {
  padding: 3rem 1.5rem;
  background: var(--v-theme-background);
  border-radius: 10px;
}

@include mobile {
  .pa-6 {
    padding: 1rem !important;
  }

  .feed-preview {
    padding: 1rem;
  }

  .hero-photo,
  .hero-photo-placeholder {
    width: 160px;
    height: 160px;
  }

  .hero-name {
    font-size: 1.5rem;
  }

  .hero-detail-item {
    font-size: 0.8rem;
  }

  .photo-row {
    flex-direction: column;
    gap: 0.5rem;
  }

  .photo-left {
    width: 100%;
  }

  .photo-right {
    padding-top: 0;
  }
}
</style>
